'use client'

import type { CompanyShippingAddress } from '@maestro/data-layer'

import { useSession } from 'next-auth/react'
import { useRouter } from 'next/navigation'
import { useCallback, useMemo, useState } from 'react'
import useSWR from 'swr'

import {
  getShippingAddresses,
  setShippingAddress,
  SHIPPING_ADDRESSES_SWR_KEY,
} from '@maestro/commerce-blocks/client/api/shipping-addresses'
import { SHIPPING_ADDRESS_SWITCHER_MODAL_KEY } from '@maestro/commerce-blocks/components/address-shipping-switcher/address-shipping-switcher.constants'
import { createLogger } from '@maestro/core/logger'
import { useModals } from '@pearlconvert/ui/providers/modals/modals.context'

const log = createLogger('maestro:commerce-blocks:shipping-address-switcher')

export const useAddressShippingSwitcherSlideIn = (onSelected: () => void) => {
  const { isOpen } = useModals()
  const { data: session, update } = useSession()
  const router = useRouter()
  const [selectingId, setSelectingId] = useState<string | undefined>()

  const isModalOpen = isOpen(SHIPPING_ADDRESS_SWITCHER_MODAL_KEY)

  const { data, isLoading } = useSWR(
    isModalOpen ? SHIPPING_ADDRESSES_SWR_KEY : null,
    getShippingAddresses,
    {
      revalidateOnFocus: false,
      revalidateIfStale: false,
      revalidateOnReconnect: false,
      onError: (error) =>
        log.error('Error fetching shipping addresses:', error),
    }
  )

  const selectedId = session?.user?.company?.shippingAddress?.id

  const addresses = useMemo<CompanyShippingAddress[]>(() => {
    return (data ?? []).map((address) => ({
      ...address,
      isSelected: address.id === selectedId,
    }))
  }, [data, selectedId])

  const selectAddress = useCallback(
    async (address: CompanyShippingAddress) => {
      setSelectingId(address.id)
      try {
        const result = await setShippingAddress(address.id)
        if (result.success) {
          await update({
            trigger: 'updateCompany',
            user: {
              company: {
                shippingAddress: address,
              },
            },
          })
          // Server-rendered views (order and invoice history) read the ship-to from the session.
          router.refresh()
          onSelected()
        }
      } catch (error) {
        log.error('Error setting shipping address:', {
          id: address.id,
          error,
        })
      } finally {
        setSelectingId(undefined)
      }
    },
    [onSelected, router, update]
  )

  return {
    addresses,
    areAddressesLoading: isModalOpen && isLoading,
    selectingId,
    selectAddress,
  }
}
